import styled from '@emotion/styled';
import range from 'lodash/range';
import React from 'react';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';

import { DateWithSessionSlots, SessionSlot } from '../models/videoSessions';

enum ScheduleCalendarSlotsListTestID {
    TimeSlot = 'ScheduleCalendarSlotsListTestID.TimeSlot',
}

const Wrapper = styled.div({
    width: '100%',
    padding: '0 32px 24px',
    marginTop: 8,
    borderBottom: `1px solid ${color.linkWaterLight}`,
});

interface InnerWrapperProps {
    disableScrollY: boolean;
}

const InnerWrapper = styled.div<InnerWrapperProps>(props => ({
    marginRight: -20,
    height: 340,
    overflowY: props.disableScrollY ? 'hidden' : 'scroll',
}));

const SlotsList = styled.div({
    display: 'flex',
    overflowX: 'hidden',
});

const SlotsColumn = styled.div({
    minWidth: 95,
    maxWidth: 95,
    marginRight: 16,
    transition: '.3s all',
});

const PlaceholderSlot = styled.div({
    width: '100%',
    height: 40,
    marginTop: 8,
    backgroundColor: hexToRgba('#e6eaef', 0.7),
    borderRadius: 4,
});

interface SlotProps {
    selected: boolean;
}

const Slot = styled(PlaceholderSlot)<SlotProps>(props => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    border: `2px solid ${color.catskillWhite}`,
    borderRadius: '6px',
    transition: '.3s all',
    backgroundColor: props.selected ? color.royalBlue : 'transparent',
    ':hover': {
        border: `1px solid ${color.royalBlue}`,
    },
    dataTestId: `${ScheduleCalendarSlotsListTestID.TimeSlot}`,
}));

const SlotText = styled.span<SlotProps>(
    props => ({
        marginTop: 5,
        color: props.selected ? color.white : color.steelGray,
        textTransform: 'lowercase',
    }),
    font.medium(font.size.medium),
);

const placeholderData = [3, 4, 1, 2, 5, 1, 3];

const Placeholder: React.FC = () => (
    <Wrapper>
        <InnerWrapper disableScrollY>
            <SlotsList>
                {placeholderData.map((slotsNumber, i) => (
                    <SlotsColumn key={`${slotsNumber}${i}`}>
                        {range(0, slotsNumber).map(slotNumber => (
                            <PlaceholderSlot key={slotNumber} />
                        ))}
                    </SlotsColumn>
                ))}
            </SlotsList>
        </InnerWrapper>
    </Wrapper>
);

enum ScheduleCalendarSlotsListTestID {
    Column = 'ScheduleCalendarSlotsListTestID.Column',
    Row = 'ScheduleCalendarSlotsListTestID.Row',
}

interface Props {
    sliderOffset: number;
    datesWithSessionSlots: DateWithSessionSlots[];
    selectedSlot: SessionSlot | null;
    onSlotClick: (slot: SessionSlot) => void;
    loading: boolean;
}

export const ScheduleCalendarSlotsList: React.FC<Props> = ({
    datesWithSessionSlots,
    loading,
    sliderOffset,
    selectedSlot,
    onSlotClick,
}) => {
    if (loading) {
        return <Placeholder />;
    }
    return (
        <Wrapper>
            <InnerWrapper disableScrollY={loading}>
                <SlotsList>
                    {datesWithSessionSlots.map(({ slots, date }, i) => (
                        <SlotsColumn
                            key={date.toString()}
                            style={{ marginLeft: i === 0 ? sliderOffset : 0 }}
                            data-testid={`${ScheduleCalendarSlotsListTestID.Column}-${date.format(
                                'dddd',
                            )}`}
                        >
                            {slots.map((slot, index) => {
                                const selected = selectedSlot
                                    ? slot.start.isSame(selectedSlot.start)
                                    : false;

                                const text = slot.start.format('LT');

                                return (
                                    <Slot
                                        key={text}
                                        selected={selected}
                                        onClick={() => onSlotClick(slot)}
                                        data-testid={`${ScheduleCalendarSlotsListTestID.Row}-${index}`}
                                    >
                                        <SlotText selected={selected}>{text}</SlotText>
                                    </Slot>
                                );
                            })}
                        </SlotsColumn>
                    ))}
                </SlotsList>
            </InnerWrapper>
        </Wrapper>
    );
};
